import { useEffect, useState, useRef } from "react";
import { projectsAPI } from "../api";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";

const ProjectsShowcase = () => {
  const scrollRef = useRef(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await projectsAPI.getAll();
        setProjects(data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-black border-y border-yellow-500/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-yellow-500/10 rounded w-64 mx-auto mb-4"></div>
            <div className="h-12 bg-yellow-500/10 rounded w-96 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (projects.length === 0) return null;

  // Duplicate for seamless infinite scroll
  const duplicatedProjects = [...projects, ...projects, ...projects];

  return (
    <section
      className="py-20 bg-black border-y border-yellow-500/20 overflow-hidden relative"
      data-testid="projects-showcase"
    >
      {/* Ambient neon background blobs */}
      <div className="absolute -top-20 -left-20 w-96 h-96 bg-yellow-500/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-orange-500/10 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 space-y-4">
          <span className="text-yellow-400 font-semibold text-sm uppercase tracking-wider">
            My Portfolio
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            Recent <span className="gradient-text">Work Showcase</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Hover over any project to explore more
          </p>
        </div>

        <div className="relative">
          {/* Subtle fade edges (much lighter than before) */}
          <div className="absolute left-0 top-0 bottom-0 w-24 md:w-32 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-24 md:w-32 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none"></div>

          <div
            ref={scrollRef}
            className="flex items-center gap-8 projects-marquee"
            style={{ width: "fit-content" }}
          >
            {duplicatedProjects.map((project, index) => (
              <div
                key={`${project.id}-${index}`}
                className="flex-shrink-0 group relative"
                data-testid="project-card"
              >
                {/* Outer animated neon glow (always on, runs continuously) */}
                <div className="neon-aura"></div>

                {/* Project Card */}
                <div className="w-80 md:w-96 rounded-2xl overflow-hidden bg-[#0a0a0a] relative neon-card transition-transform duration-500 group-hover:-translate-y-2">
                  {/* Project Image — NO heavy overlay anymore */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={project.image || "https://images.unsplash.com/photo-1460925895917-afdab827c52f"}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1460925895917-afdab827c52f";
                      }}
                    />

                    {/* Soft bottom gradient ONLY for text legibility (kept very light) */}
                    <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent pointer-events-none"></div>

                    {/* Category Badge with continuous neon pulse */}
                    <div className="absolute top-4 left-4">
                      <span className="px-4 py-2 bg-yellow-400 text-black text-xs font-bold rounded-full neon-badge">
                        {project.category || "Project"}
                      </span>
                    </div>

                    {/* Always-animating corner neon lights */}
                    <div className="neon-corner top-left"></div>
                    <div className="neon-corner top-right"></div>
                    <div className="neon-corner bottom-left"></div>
                    <div className="neon-corner bottom-right"></div>
                  </div>

                  {/* Project Info */}
                  <div className="p-6 space-y-3 bg-[#0a0a0a]">
                    <h3 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors duration-300 line-clamp-1">
                      {project.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed line-clamp-2">
                      {project.description || "Explore this project crafted with precision and creativity."}
                    </p>

                    {/* Animated golden divider — always visible */}
                    <div className="neon-divider"></div>

                    {/* CTAs — show on hover */}
                    <div className="flex gap-3 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                      <Button
                        className="flex-1 bg-yellow-400 hover:bg-yellow-300 text-black font-bold shadow-lg hover:shadow-[0_0_24px_rgba(230,194,0,0.7)] transition-all duration-300"
                        onClick={() => {
                          if (project.link) {
                            window.open(project.link, "_blank", "noopener,noreferrer");
                          }
                        }}
                      >
                        <ArrowUpRight className="mr-2" size={16} />
                        Let's Create Yours
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 border-2 border-yellow-500 text-yellow-400 hover:bg-yellow-400 hover:text-black shadow-lg hover:shadow-[0_0_20px_rgba(230,194,0,0.6)] transition-all duration-300"
                        onClick={() => { window.location.href = "/contact"; }}
                      >
                        <ExternalLink className="mr-2" size={16} />
                        Get Quote
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <Button
            size="lg"
            className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-8 py-6 text-lg shadow-xl hover:shadow-[0_0_30px_rgba(230,194,0,0.6)] transition-all duration-300"
            onClick={() => (window.location.href = "/portfolio")}
            data-testid="view-portfolio-btn"
          >
            View Full Portfolio
            <ArrowUpRight className="ml-2" size={20} />
          </Button>
        </div>
      </div>

      <style jsx>{`
        @keyframes scroll-projects {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .projects-marquee {
          animation: scroll-projects 40s linear infinite;
        }
        .projects-marquee:hover {
          animation-play-state: paused;
        }

        /* Outer continuous neon aura around card */
        @keyframes neon-aura {
          0%, 100% {
            box-shadow:
              0 0 18px rgba(230, 194, 0, 0.55),
              0 0 36px rgba(230, 194, 0, 0.35),
              0 0 60px rgba(232, 123, 53, 0.25);
            opacity: 0.9;
          }
          50% {
            box-shadow:
              0 0 28px rgba(232, 123, 53, 0.7),
              0 0 60px rgba(230, 194, 0, 0.5),
              0 0 90px rgba(230, 194, 0, 0.3);
            opacity: 1;
          }
        }
        .neon-aura {
          position: absolute;
          inset: -3px;
          border-radius: 1.1rem;
          pointer-events: none;
          animation: neon-aura 2.6s ease-in-out infinite;
          z-index: 0;
        }
        .neon-card { z-index: 1; }
        .group:hover .neon-aura {
          animation-duration: 1.4s;
        }

        /* Continuous badge pulse */
        @keyframes neon-pulse-badge {
          0%, 100% {
            box-shadow: 0 0 8px rgba(230, 194, 0, 0.9), 0 0 18px rgba(230, 194, 0, 0.6);
          }
          50% {
            box-shadow: 0 0 16px rgba(232, 123, 53, 0.9), 0 0 32px rgba(230, 194, 0, 0.7);
          }
        }
        .neon-badge { animation: neon-pulse-badge 2s ease-in-out infinite; }

        /* Continuously running corner lights with different delays */
        @keyframes corner-twinkle {
          0%, 100% { opacity: 0.25; transform: scale(1); }
          50%      { opacity: 0.9;  transform: scale(1.1); }
        }
        .neon-corner {
          position: absolute;
          width: 5rem;
          height: 5rem;
          pointer-events: none;
          mix-blend-mode: screen;
        }
        .neon-corner.top-left {
          top: 0; left: 0;
          background: radial-gradient(circle at top left, rgba(230, 194, 0, 0.75), transparent 70%);
          animation: corner-twinkle 2.4s ease-in-out infinite;
        }
        .neon-corner.top-right {
          top: 0; right: 0;
          background: radial-gradient(circle at top right, rgba(232, 123, 53, 0.75), transparent 70%);
          animation: corner-twinkle 2.4s ease-in-out infinite 0.6s;
        }
        .neon-corner.bottom-left {
          bottom: 0; left: 0;
          background: radial-gradient(circle at bottom left, rgba(232, 123, 53, 0.75), transparent 70%);
          animation: corner-twinkle 2.4s ease-in-out infinite 1.2s;
        }
        .neon-corner.bottom-right {
          bottom: 0; right: 0;
          background: radial-gradient(circle at bottom right, rgba(230, 194, 0, 0.75), transparent 70%);
          animation: corner-twinkle 2.4s ease-in-out infinite 1.8s;
        }

        /* Always-visible animated golden divider */
        @keyframes divider-flow {
          0%, 100% { background-position: 0% 50%; opacity: 0.7; }
          50%      { background-position: 100% 50%; opacity: 1; }
        }
        .neon-divider {
          height: 2px;
          background: linear-gradient(90deg, transparent 0%, #e6c200 30%, #E87B35 50%, #e6c200 70%, transparent 100%);
          background-size: 200% 100%;
          animation: divider-flow 3s ease-in-out infinite;
          border-radius: 2px;
          box-shadow: 0 0 8px rgba(230, 194, 0, 0.6);
        }
      `}</style>
    </section>
  );
};

export default ProjectsShowcase;
